import React, { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Flex,
  HStack,
  VStack,
  Link,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useColorModeValue,
  useColorMode,
  Stack,
  Avatar,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Tooltip,
  Divider,
} from '@chakra-ui/react'
import { 
  HamburgerIcon, 
  CloseIcon, 
  SearchIcon, 
  BellIcon, 
  ChevronDownIcon,
  SettingsIcon,
  StarIcon,
  SunIcon,
  MoonIcon,
} from '@chakra-ui/icons'
import { useAuth } from '../contexts/AuthContext'
import { NavbarLogo } from './SpeedcodeLogo'

const Links = [
  { name: 'Problems', href: '/problems' },
  { name: 'Leaderboard', href: '/leaderboard' },
  { name: 'Contests', href: '/contests' },
  { name: 'Documentation', href: '/docs' },
]

const NavLink = ({ children, href }: { children: React.ReactNode; href: string }) => {
  const linkColor = useColorModeValue('gray.600', 'gray.300')
  
  return (
    <Link
      as={RouterLink}
      px={4}
      py={2}
      rounded={'xl'}
      fontWeight="600"
      fontSize="sm"
      color={linkColor}
    position="relative"
    transition="all 0.2s ease"
    _active={{
      bg: 'rgba(59, 130, 246, 0.08)',
      color: 'brand.600',
    }}
    _before={{
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '0%',
      height: '2px',
      background: 'linear-gradient(90deg, brand.500 0%, secondary.500 100%)',
      borderRadius: '1px',
      transition: 'width 0.3s ease',
    }}
    _hover={{
      textDecoration: 'none',
      bg: 'rgba(59, 130, 246, 0.06)',
      color: 'brand.600',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
      _before: {
        width: '70%',
      },
    }}
      to={href}
    >
      {children}
    </Link>
  )
}

export default function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { user, isAuthenticated, login, logout } = useAuth()
  const { colorMode, toggleColorMode } = useColorMode()
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Implement search functionality
    
  }

  const navBg = useColorModeValue("rgba(255, 255, 255, 0.92)", "rgba(26, 32, 44, 0.92)")
  const borderColor = useColorModeValue("rgba(0, 0, 0, 0.05)", "rgba(255, 255, 255, 0.05)")

  return (
    <Box 
      px={6} 
      position="relative"
      zIndex={1000}
      bg={navBg}
      backdropFilter="blur(20px)"
      boxShadow={useColorModeValue(
        "0 8px 32px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
        "0 8px 32px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)"
      )}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: useColorModeValue(
          `linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(246,249,254,0.95) 50%, rgba(242,246,252,0.98) 100%),
           radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.10) 0%, transparent 65%),
           radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.08) 0%, transparent 65%)`,
          `linear-gradient(135deg, rgba(26,32,44,0.98) 0%, rgba(22,27,38,0.95) 50%, rgba(19,23,34,0.98) 100%),
           radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.15) 0%, transparent 65%),
           radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.12) 0%, transparent 65%)`
        ),
        zIndex: -1,
      }}
      _after={{
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '0.5px',
        background: useColorModeValue(
          'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.08) 50%, transparent 100%)',
          'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.15) 50%, transparent 100%)'
        ),
        zIndex: -1,
      }}
    >
      <Flex h={20} alignItems={'center'} justifyContent={'space-between'} maxW="container.xl" mx="auto">
        {/* Mobile Menu Button */}
        <IconButton
          size={'md'}
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          aria-label={'Open Menu'}
          display={{ md: 'none' }}
          onClick={isOpen ? onClose : onOpen}
          variant="ghost"
          color={useColorModeValue('gray.600', 'gray.300')}
          bg={useColorModeValue('rgba(255, 255, 255, 0.7)', 'rgba(45, 55, 72, 0.7)')}
          backdropFilter="blur(10px)"
          border="1px solid"
          borderColor={useColorModeValue('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.01)')}
          _hover={{ 
            bg: useColorModeValue('rgba(59, 130, 246, 0.08)', 'rgba(59, 130, 246, 0.15)'), 
            color: 'brand.600',
            borderColor: 'brand.300'
          }}
          transition="all 0.2s ease"
          borderRadius="lg"
        />

        {/* Logo and Brand */}
        <HStack spacing={10} alignItems={'center'}>
          <Link
            as={RouterLink}
            to="/"
            _hover={{
              transform: 'scale(1.005)',
              transition: 'transform 0.2s ease',
              textDecoration: 'none',
            }}
            _active={{
              transform: 'scale(0.995)',
            }}
          >
            <HStack spacing={3}>
              <NavbarLogo width="50px" height="30px" />
              <Text 
                fontSize="2xl" 
                fontWeight="800" 
                fontFamily="'Poppins', 'Montserrat', 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
                bgGradient="linear(135deg, brand.500 0%, secondary.500 100%)"
                bgClip="text"
                letterSpacing="-0.02em"
                filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))"
                cursor="pointer"
              >
                Speedcode
              </Text>
            </HStack>
          </Link>
          
          {/* Search Bar - Desktop */}
          <Box display={{ base: 'none', lg: 'block' }} minW="320px">
            <form onSubmit={handleSearch}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={useColorModeValue('gray.400', 'gray.500')} />
                </InputLeftElement>
                <Input
                  placeholder="Search problems, algorithms, or topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg={useColorModeValue("rgba(255, 255, 255, 0.9)", "rgba(45, 55, 72, 0.9)")}
                  borderColor={useColorModeValue("rgba(0, 0, 0, 0.1)", "rgba(255, 255, 255, 0.015)")}
                  borderWidth="1.5px"
                  borderRadius="2xl"
                  fontSize="sm"
                  fontWeight="500"
                  backdropFilter="blur(15px)"
                  boxShadow={useColorModeValue(
                    "0 4px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                    "0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)"
                  )}
                  _hover={{ 
                    borderColor: 'brand.300',
                    bg: useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(45, 55, 72, 0.95)'),
                    boxShadow: useColorModeValue(
                      '0 8px 24px rgba(59, 130, 246, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                      '0 8px 24px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                    )
                  }}
                  _focus={{ 
                    borderColor: 'brand.500', 
                    bg: useColorModeValue('white', 'gray.700'),
                    boxShadow: useColorModeValue(
                      '0 0 0 4px rgba(59, 130, 246, 0.1), 0 8px 24px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                      '0 0 0 4px rgba(59, 130, 246, 0.2), 0 8px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                    )
                  }}
                  transition="all 0.2s ease"
                  _placeholder={{ color: useColorModeValue('gray.500', 'gray.400'), fontWeight: '400' }}
                />
              </InputGroup>
            </form>
          </Box>
          
          {/* Navigation Links - Desktop */}
          <HStack as={'nav'} spacing={2} display={{ base: 'none', md: 'flex' }}>
            {Links.map((link) => (
              <NavLink key={link.name} href={link.href}>
                {link.name}
              </NavLink>
            ))}
          </HStack>
        </HStack>

        {/* Right Side Actions */}
        <HStack spacing={4} alignItems={'center'}>
          {/* Notifications - Desktop */}
          <Tooltip label="Notifications" placement="bottom">
            <IconButton
              aria-label="Notifications"
              icon={<BellIcon />}
              variant="ghost"
              color={useColorModeValue('gray.500', 'gray.300')}
              size="md"
              borderRadius="xl"
              bg={useColorModeValue('rgba(255, 255, 255, 0.6)', 'rgba(45, 55, 72, 0.6)')}
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor={useColorModeValue('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.01)')}
              transition="all 0.2s ease"
              _hover={{ 
                bg: useColorModeValue('rgba(59, 130, 246, 0.08)', 'rgba(59, 130, 246, 0.15)'), 
                color: 'brand.600',
                boxShadow: useColorModeValue(
                  '0 8px 24px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                  '0 8px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.02)'
                ),
                borderColor: 'brand.300'
              }}
              display={{ base: 'none', md: 'flex' }}
            />
          </Tooltip>

          {/* Theme Toggle - Desktop */}
          <Tooltip label={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'} placement="bottom">
            <IconButton
              aria-label="Toggle theme"
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              variant="ghost"
              color={useColorModeValue('gray.500', 'gray.300')}
              size="md"
              borderRadius="xl"
              bg={useColorModeValue('rgba(255, 255, 255, 0.6)', 'rgba(45, 55, 72, 0.6)')}
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor={useColorModeValue('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.01)')}
              onClick={toggleColorMode}
              transition="all 0.2s ease"
              _hover={{ 
                bg: useColorModeValue('rgba(59, 130, 246, 0.08)', 'rgba(59, 130, 246, 0.15)'), 
                color: 'brand.600',
                boxShadow: useColorModeValue(
                  '0 8px 24px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                  '0 8px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.02)'
                ),
                borderColor: 'brand.300'
              }}
              display={{ base: 'none', md: 'flex' }}
            />
          </Tooltip>

          {/* User Profile */}
          {isAuthenticated ? (
            <Menu>
              <MenuButton
                as={Button}
                rounded={'xl'}
                variant={'ghost'}
                cursor={'pointer'}
                minW={0}
                px={4}
                py={2}
                border="1px solid"
                borderColor={useColorModeValue('rgba(0, 0, 0, 0.08)', 'rgba(255, 255, 255, 0.01)')}
                bg={useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(45, 55, 72, 0.8)')}
                backdropFilter="blur(15px)"
                boxShadow={useColorModeValue(
                  "0 4px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                  "0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)"
                )}
                transition="all 0.2s ease"
                _hover={{ 
                  bg: useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(45, 55, 72, 0.95)'),
                  borderColor: 'brand.300',
                  boxShadow: useColorModeValue(
                    '0 8px 24px rgba(59, 130, 246, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                    '0 8px 24px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                  )
                }}
              >
                <HStack spacing={3}>
                  <Avatar 
                    size={'sm'} 
                    src={user?.picture || ''} 
                    name={user?.name || 'User'}
                    border="2px solid"
                    borderColor="brand.100"
                  />
                  <VStack spacing={0} align="start" display={{ base: 'none', lg: 'flex' }}>
                    <Text fontSize="sm" fontWeight="700" color={useColorModeValue('gray.800', 'gray.100')}>
                      {user?.name || 'User'}
                    </Text>
                    <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} fontWeight="500">
                      Level 5 • 127 solved
                    </Text>
                  </VStack>
                  <ChevronDownIcon color={useColorModeValue('gray.600', 'gray.400')} />
                </HStack>
              </MenuButton>
              <MenuList>
                <MenuItem>
                  <HStack spacing={3}>
                    <Avatar size="sm" src={user?.picture || ''} name={user?.name || 'User'} />
                                      <VStack align="start" spacing={0}>
                    <Text fontWeight="600">{user?.name}</Text>
                    <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>{user?.email}</Text>
                  </VStack>
                  </HStack>
                </MenuItem>
                <MenuDivider />
                <MenuItem as={RouterLink} to="/profile" icon={<SettingsIcon />}>
                  Profile
                </MenuItem>
                <MenuItem icon={<StarIcon />}>
                  <Text>My Progress</Text>
                  <Badge ml="auto" colorScheme="brand" variant="subtle">127</Badge>
                </MenuItem>
                <MenuItem icon={<SettingsIcon />}>
                  Settings
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={logout} color="red.600">
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>
          ) : (
            <Button 
              onClick={login} 
              bgGradient="linear(135deg, brand.500 0%, secondary.500 100%)"
              color="white"
              variant="solid" 
              fontWeight="700"
              size="md"
              px={6}
              borderRadius="xl"
              boxShadow="0 2px 8px rgba(59, 130, 246, 0.15)"
              _hover={{
                bgGradient: "linear(135deg, brand.600 0%, secondary.600 100%)",
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.25)'
              }}
              _active={{
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.2)'
              }}
              transition="all 0.2s ease"
            >
              Login
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Mobile Menu */}
      {isOpen ? (
        <Box pb={4} display={{ md: 'none' }}>
          <VStack spacing={4} align="stretch">
            {/* Search Bar - Mobile */}
            <Box>
              <form onSubmit={handleSearch}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color={useColorModeValue('gray.400', 'gray.500')} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search problems..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg={useColorModeValue("white", "gray.700")}
                    borderColor={useColorModeValue("gray.300", "gray.600")}
                    _placeholder={{ color: useColorModeValue('gray.500', 'gray.400'), fontWeight: '400' }}
                  />
                </InputGroup>
              </form>
            </Box>
            
            {/* Navigation Links - Mobile */}
            <VStack as={'nav'} spacing={2} align="stretch">
              {Links.map((link) => (
                <NavLink key={link.name} href={link.href}>
                  {link.name}
                </NavLink>
              ))}
            </VStack>
            
            {/* Mobile Actions */}
            {isAuthenticated && (
              <>
                <Divider />
                <HStack spacing={4}>
                  <IconButton
                    aria-label="Notifications"
                    icon={<BellIcon />}
                    variant="ghost"
                    color="gray.600"
                    size="md"
                  />
                  <Text fontSize="sm" color={useColorModeValue('gray.700', 'gray.300')}>
                    Notifications
                  </Text>
                </HStack>
                <HStack spacing={4}>
                  <IconButton
                    aria-label="Toggle theme"
                    icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
                    variant="ghost"
                    color="gray.600"
                    size="md"
                    onClick={toggleColorMode}
                  />
                  <Text fontSize="sm" color={useColorModeValue('gray.700', 'gray.300')}>
                    {colorMode === 'light' ? 'Dark mode' : 'Light mode'}
                  </Text>
                </HStack>
              </>
            )}
          </VStack>
        </Box>
      ) : null}
    </Box>
  )
} 